import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CreditCard,
  Search,
  Download,
  Eye,
  Edit,
  Truck,
  Package,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Loader2,
  Camera,
  ArrowUpCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import AdvancedCardOCR from "@/components/AdvancedCardOCR";
import Pagination from "@/components/Pagination";

export default function Cards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedCard, setSelectedCard] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [exportFilter, setExportFilter] = useState("all"); // Export filter state

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Status update form
  const [statusUpdate, setStatusUpdate] = useState({
    status: "",
    notes: "",
  });

  // Shipping update form
  const [shippingUpdate, setShippingUpdate] = useState({
    carrier: "",
    trackingNumber: "",
    status: "shipped",
  });

  useEffect(() => {
    loadCards();
  }, [statusFilter]);

  const loadCards = async () => {
    try {
      let query = supabase
        .from("cards")
        .select(
          `
          *,
          accounts!cards_account_id_fkey (
            email,
            first_name,
            last_name
          ),
          card_activity_logs (
            activity_type,
            activity_description,
            created_at
          )
        `
        )
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("card_status", statusFilter);
      }

      const { data: cardsData, error: cardsError } = await query;

      if (cardsError) throw cardsError;

      // Separately fetch topups data for each card
      const cardsWithTopups = await Promise.all(
        (cardsData || []).map(async (card) => {
          // Only fetch topups if card has a card_number
          if (card.card_number) {
            const { data: topupsData, error: topupsError } = await supabase
              .from("topups")
              .select("amount, fee, total_amount, status, created_at")
              .eq("card_number", card.card_number)
              .order("created_at", { ascending: false });

            if (!topupsError && topupsData) {
              return { ...card, topups: topupsData };
            }
          }
          return { ...card, topups: [] };
        })
      );

      setCards(cardsWithTopups);
    } catch (error) {
      console.error("Error loading cards:", error);
      toast.error(`Failed to load cards: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedCard || !statusUpdate.status) return;

    try {
      const { data, error } = await supabase.rpc("update_card_status", {
        p_card_uuid: selectedCard.id,
        p_status: statusUpdate.status,
        p_notes: statusUpdate.notes,
      });

      if (error) throw error;

      toast.success("Card status has been updated");
      setShowStatusModal(false);
      setStatusUpdate({ status: "", notes: "" });
      loadCards();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleShippingUpdate = async () => {
    if (
      !selectedCard ||
      !shippingUpdate.carrier ||
      !shippingUpdate.trackingNumber
    ) {
      toast.error("Please enter carrier and tracking number");
      return;
    }

    try {
      const { data, error } = await supabase.rpc("update_card_shipping", {
        p_order_id: selectedCard.fellas_order_id,
        p_carrier: shippingUpdate.carrier,
        p_tracking_number: shippingUpdate.trackingNumber,
        p_status: shippingUpdate.status,
      });

      if (error) throw error;

      toast.success("Shipping information has been updated");
      setShowShippingModal(false);
      setShippingUpdate({ carrier: "", trackingNumber: "", status: "shipped" });
      loadCards();
    } catch (error) {
      console.error("Error updating shipping:", error);
      toast.error("Failed to update shipping information");
    }
  };

  const handleExportToExcel = () => {
    try {
      // Filter cards based on export filter
      let filteredCards = cards;
      if (exportFilter === "not_shipped") {
        filteredCards = cards.filter((card) => !card.shipped_at);
      } else if (exportFilter === "shipped") {
        filteredCards = cards.filter((card) => card.shipped_at);
      }

      if (filteredCards.length === 0) {
        toast.warning("No cards to export with the selected filter");
        return;
      }

      // Prepare data for export
      const exportData = filteredCards.map((card) => ({
        "Order ID": card.fellas_order_id || "",
        Username:
          card.accounts?.first_name && card.accounts?.last_name
            ? `${card.accounts.first_name} ${card.accounts.last_name}`
            : "",
        Email: card.accounts?.email || "",
        "Shipping Name": card.first_name + " " + card.last_name || "",
        "Shipping Address2": card.shipping_address2 || "",
        "Shipping Address1": card.shipping_address1 || "",
        "Shipping City": card.shipping_city || "",
        "Shipping State": card.shipping_state || "",
        "Shipping Zip": card.shipping_zip_code || "",
        "Shipping Country": card.nationality || "",
        "Shipping Phone": card.phone_number || "",
        Carrier: card.carrier || "",
        "Tracking Number": card.tracking_number || "",
        "Card Status": card.card_status || "",
        "Buy Date": card.created_at
          ? new Date(card.created_at).toLocaleDateString()
          : "",
        "Shipped Date": card.shipped_at
          ? new Date(card.shipped_at).toLocaleDateString()
          : "Not Shipped",
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Cards");

      // Generate filename with current date and filter type
      const date = new Date().toISOString().split("T")[0];
      const filterSuffix = exportFilter === "all" ? "" : `_${exportFilter}`;
      const filename = `cards_export_${date}${filterSuffix}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      toast.success(`Exported ${exportData.length} cards to Excel`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export data");
    }
  };

  const handleOCRDataExtracted = async (ocrData) => {
    if (!selectedCard) return;

    try {
      // Hash the CVC for security
      const cvcHash = ocrData.cvc ? btoa(ocrData.cvc) : null;

      // Update card with OCR data
      const { error } = await supabase
        .from("cards")
        .update({
          card_number: ocrData.cardNumber,
          expiration_date: ocrData.expirationDate,
          cvc_hash: cvcHash,
          proxy_number: ocrData.proxyNumber || null,
          is_registered: true,
          registered_at: new Date().toISOString(),
          card_status: "active",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedCard.id);

      if (error) throw error;

      // Log activity
      await supabase.from("card_activity_logs").insert({
        card_id: selectedCard.id,
        activity_type: "card_registered",
        activity_description: "Card information registered via OCR",
        metadata: {
          registration_method: "ocr",
          has_proxy: !!ocrData.proxyNumber,
        },
      });

      toast.success("Card information has been registered successfully!");
      setShowOCRModal(false);
      loadCards();
    } catch (error) {
      console.error("Error saving OCR data:", error);
      toast.error("Failed to save card information");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: "secondary", icon: Clock, label: "Pending" },
      paid: { variant: "default", icon: DollarSign, label: "Paid" },
      processing: { variant: "default", icon: Package, label: "Processing" },
      shipped: { variant: "default", icon: Truck, label: "Shipped" },
      delivered: { variant: "default", icon: CheckCircle, label: "Delivered" },
      activating: { variant: "warning", icon: Clock, label: "Activating" },
      active: { variant: "success", icon: CheckCircle, label: "Active" },
      blocked: { variant: "destructive", icon: XCircle, label: "Blocked" },
      frozen: { variant: "destructive", icon: AlertCircle, label: "Frozen" },
      cancelled: { variant: "secondary", icon: XCircle, label: "Cancelled" },
      expired: { variant: "secondary", icon: Clock, label: "Expired" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredCards = cards.filter((card) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      card.email?.toLowerCase().includes(searchLower) ||
      card.first_name?.toLowerCase().includes(searchLower) ||
      card.last_name?.toLowerCase().includes(searchLower) ||
      card.fellas_order_id?.toString().includes(searchQuery) ||
      card.mimbo_username?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredCards.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCards = filteredCards.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, pageSize]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card className="border-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Card Management</h2>
            <p className="text-muted-foreground">
              Manage FellasCard orders and activations
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={exportFilter} onValueChange={setExportFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Export filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cards</SelectItem>
                <SelectItem value="not_shipped">Not Shipped</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExportToExcel}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email, name, or order ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Cards Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Registered</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <div className="text-muted-foreground">
                      <CreditCard className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p className="text-lg font-medium">
                        No cards registered yet
                      </p>
                      <p className="text-sm mt-1">
                        Card orders will appear here when users purchase cards.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCards.map((card) => (
                  <TableRow key={card.id}>
                    <TableCell className="font-mono text-xs">
                      {card.fellas_order_id}
                    </TableCell>
                    <TableCell className="font-medium">
                      {card.mimbo_username || "-"}
                    </TableCell>
                    <TableCell className="text-xs">
                      <span
                        className={
                          card.source === "lumy"
                            ? "font-semibold text-cyan-600"
                            : "font-medium text-gray-600"
                        }
                      >
                        {card.source || "mimbo"}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{card.email}</TableCell>
                    <TableCell>{getStatusBadge(card.card_status)}</TableCell>
                    <TableCell>
                      {card.is_registered ? (
                        <Badge variant="success" className="text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          <XCircle className="h-3 w-3 mr-1" />
                          No
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {card.is_registered && card.card_balance !== null ? (
                        <span className="text-sm">
                          ${card.card_balance.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {card.payment_status === "completed" ? (
                        <Badge variant="success" className="text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Paid
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {new Date(card.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedCard(card);
                            setShowDetailsModal(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedCard(card);
                            setStatusUpdate({
                              status: card.card_status,
                              notes: "",
                            });
                            setShowStatusModal(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {card.payment_status === "completed" &&
                          !card.tracking_number && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedCard(card);
                                setShowShippingModal(true);
                              }}
                            >
                              <Truck className="h-4 w-4" />
                            </Button>
                          )}
                        {!card.is_registered && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCard(card);
                              setShowCameraModal(true);
                            }}
                            title="Scan card with OCR"
                          >
                            <Camera className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {filteredCards.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filteredCards.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
          />
        )}
      </CardContent>

      {/* Card Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Card Details</DialogTitle>
            <DialogDescription>
              Order ID: {selectedCard?.fellas_order_id}
            </DialogDescription>
          </DialogHeader>

          {selectedCard && (
            <div className="space-y-4">
              <Tabs defaultValue="info">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="info">Information</TabsTrigger>
                  <TabsTrigger value="shipping">Shipping</TabsTrigger>
                  <TabsTrigger value="registration">Registration</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        MimboNode Username
                      </Label>
                      <p className="text-sm mt-1 font-medium">
                        {selectedCard.mimbo_username || "N/A"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Email
                      </Label>
                      <p className="text-sm mt-1">{selectedCard.email}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Name
                      </Label>
                      <p className="text-sm mt-1">
                        {selectedCard.first_name} {selectedCard.middle_name}{" "}
                        {selectedCard.last_name}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Nationality
                      </Label>
                      <p className="text-sm mt-1">{selectedCard.nationality}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Phone
                      </Label>
                      <p className="text-sm mt-1">
                        {selectedCard.phone_number}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Card Status
                      </Label>
                      <div className="text-sm mt-1">
                        {getStatusBadge(selectedCard.card_status)}
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Payment Status
                      </Label>
                      <div className="text-sm mt-1">
                        {selectedCard.payment_status === "completed" ? (
                          <Badge variant="success">Completed</Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                      </div>
                    </div>
                    {selectedCard.payment_tx_hash && (
                      <div className="col-span-2">
                        <Label className="text-muted-foreground text-xs">
                          Transaction Hash
                        </Label>
                        <p className="text-xs font-mono mt-1">
                          {selectedCard.payment_tx_hash}
                        </p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="shipping" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-muted-foreground text-xs">
                        Shipping Address
                      </Label>
                      <p className="text-sm mt-1">
                        {selectedCard.shipping_address1}
                        <br />
                        {selectedCard.shipping_address2 && (
                          <>
                            {selectedCard.shipping_address2}
                            <br />
                          </>
                        )}
                        {selectedCard.shipping_city},{" "}
                        {selectedCard.shipping_state}{" "}
                        {selectedCard.shipping_zip_code}
                        <br />
                        {selectedCard.shipping_country}
                      </p>
                    </div>
                    {selectedCard.shipping_carrier && (
                      <>
                        <div>
                          <Label className="text-muted-foreground text-xs">
                            Carrier
                          </Label>
                          <p className="text-sm mt-1">
                            {selectedCard.shipping_carrier}
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">
                            Tracking Number
                          </Label>
                          <p className="text-sm mt-1">
                            {selectedCard.tracking_number}
                          </p>
                        </div>
                      </>
                    )}
                    {selectedCard.shipped_at && (
                      <div>
                        <Label className="text-muted-foreground text-xs">
                          Shipped Date
                        </Label>
                        <p className="text-sm mt-1">
                          {new Date(selectedCard.shipped_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedCard.delivered_at && (
                      <div>
                        <Label className="text-muted-foreground text-xs">
                          Delivered Date
                        </Label>
                        <p className="text-sm mt-1">
                          {new Date(selectedCard.delivered_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="registration" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Card Number
                      </Label>
                      <p className="text-sm mt-1 font-mono">
                        {selectedCard.card_number
                          ? `****-****-****-${selectedCard.card_number.slice(
                              -4
                            )}`
                          : "Not registered"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Registration Status
                      </Label>
                      <div className="text-sm">
                        {selectedCard.is_registered ? (
                          <Badge variant="success">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Registered
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <Clock className="h-3 w-3 mr-1" />
                            Not Registered
                          </Badge>
                        )}
                      </div>
                    </div>
                    {selectedCard.expiration_date && (
                      <div>
                        <Label className="text-muted-foreground text-xs">
                          Expiration Date
                        </Label>
                        <p className="text-sm mt-1">
                          {selectedCard.expiration_date}
                        </p>
                      </div>
                    )}
                    {selectedCard.registered_at && (
                      <div>
                        <Label className="text-muted-foreground text-xs">
                          Registered At
                        </Label>
                        <p className="text-sm mt-1">
                          {new Date(
                            selectedCard.registered_at
                          ).toLocaleString()}
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Card Balance
                      </Label>
                      <p className="text-sm mt-1 font-medium">
                        ${selectedCard.card_balance?.toFixed(2) || "0.00"} USDT
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Total Top-ups
                      </Label>
                      <p className="text-sm mt-1 font-medium">
                        ${selectedCard.total_topup_amount?.toFixed(2) || "0.00"}{" "}
                        USDT
                      </p>
                    </div>
                    {selectedCard.last_topup_at && (
                      <div>
                        <Label className="text-muted-foreground text-xs">
                          Last Top-up
                        </Label>
                        <p className="text-sm mt-1">
                          {new Date(
                            selectedCard.last_topup_at
                          ).toLocaleString()}
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        Card Features
                      </Label>
                      <div className="flex gap-2 mt-1">
                        {selectedCard.atm_enabled && (
                          <Badge variant="outline" className="text-xs">
                            ATM
                          </Badge>
                        )}
                        {selectedCard.online_enabled && (
                          <Badge variant="outline" className="text-xs">
                            Online
                          </Badge>
                        )}
                        {selectedCard.contactless_enabled && (
                          <Badge variant="outline" className="text-xs">
                            Contactless
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Top-up History */}
                  {selectedCard.topups && selectedCard.topups.length > 0 && (
                    <div className="mt-4">
                      <Label className="mb-2 block">Top-up History</Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                        {selectedCard.topups.map((topup, index) => (
                          <div
                            key={index}
                            className="flex items-start gap-2 p-2 rounded-lg bg-muted/50"
                          >
                            <ArrowUpCircle className="h-4 w-4 mt-0.5 text-green-600" />
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <span className="text-sm font-medium">
                                  ${topup.amount.toFixed(2)} USDT
                                </span>
                                <Badge
                                  variant={
                                    topup.status === "completed"
                                      ? "success"
                                      : topup.status === "failed"
                                      ? "destructive"
                                      : "secondary"
                                  }
                                  className="text-xs"
                                >
                                  {topup.status}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Fee: ${topup.fee.toFixed(2)} | Total: $
                                {topup.total_amount.toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(topup.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="space-y-2">
                  {selectedCard.card_activity_logs?.map((log, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 p-2 rounded-lg bg-muted/50"
                    >
                      <div className="flex-1">
                        <p className="text-sm mt-1 font-medium">
                          {log.activity_type}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {log.activity_description}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Card Status</DialogTitle>
            <DialogDescription>
              Change the status of card {selectedCard?.fellas_order_id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground text-xs">Status</Label>
              <Select
                value={statusUpdate.status}
                onValueChange={(value) =>
                  setStatusUpdate({ ...statusUpdate, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="activating">Activating</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="frozen">Frozen</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-muted-foreground text-xs">Notes</Label>
              <Textarea
                placeholder="Add notes about this status change..."
                value={statusUpdate.notes}
                onChange={(e) =>
                  setStatusUpdate({ ...statusUpdate, notes: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shipping Update Modal */}
      <Dialog open={showShippingModal} onOpenChange={setShowShippingModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Shipping Information</DialogTitle>
            <DialogDescription>
              Add shipping details for {selectedCard?.fellas_order_id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground text-xs">
                Shipping Carrier
              </Label>
              <Input
                placeholder="e.g., DHL, FedEx, UPS"
                value={shippingUpdate.carrier}
                onChange={(e) =>
                  setShippingUpdate({
                    ...shippingUpdate,
                    carrier: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <Label className="text-muted-foreground text-xs">
                Tracking Number
              </Label>
              <Input
                placeholder="Enter tracking number"
                value={shippingUpdate.trackingNumber}
                onChange={(e) =>
                  setShippingUpdate({
                    ...shippingUpdate,
                    trackingNumber: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <Label className="text-muted-foreground text-xs">Status</Label>
              <Select
                value={shippingUpdate.status}
                onValueChange={(value) =>
                  setShippingUpdate({ ...shippingUpdate, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowShippingModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleShippingUpdate}>Update Shipping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advanced Card OCR with Layout Management - Direct Camera Integration */}
      <AdvancedCardOCR
        isOpen={showCameraModal}
        onClose={() => {
          setShowCameraModal(false);
        }}
        onCardDataExtracted={handleOCRDataExtracted}
        cardId={selectedCard?.id}
        startWithCamera={true}
      />
    </Card>
  );
}
